import {
    EmployeePickerElement,
    EmployeePickerForm,
    RequiredFieldElement,
    RequiredFieldForm,
    RequiredFieldScope,
    SettingListElement,
    SettingScope,
} from 'types/back-office-settings';
import { portalAPI } from './portalAPI';

const APIBackOfficeSettingsSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['BackOfficeSettings', 'RequiredFields', 'EmployeePickers', 'EmployeesMin'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getBackOfficeSettings: builder.query<SettingListElement[], void>({
                query: () => '/v1/back-office-settings',
                providesTags: ['BackOfficeSettings'],
            }),
            // The switch and the ticks show the change at once and a failed request takes it back. They leave the list
            // alone: a list re-read landing between two quick ticks would undo the second. The switch and the scope
            // re-read the Required fields, which leave out a setting's cells while it is off for the caller.
            setBackOfficeSettingEnabled: builder.mutation<void, { key: string; enabled: boolean }>({
                query: ({ key, enabled }) => ({
                    url: `/v1/back-office-settings/${encodeURIComponent(key)}`,
                    method: 'PUT',
                    body: { enabled },
                }),
                invalidatesTags: ['RequiredFields'],
                async onQueryStarted({ key, enabled }, { dispatch, queryFulfilled }) {
                    const patch = dispatch(
                        APIBackOfficeSettingsSlice.util.updateQueryData('getBackOfficeSettings', undefined, (draft) => {
                            const setting = draft.find((candidate) => candidate.key === key);
                            if (setting) setting.enabled = enabled;
                        })
                    );
                    try {
                        await queryFulfilled;
                    } catch {
                        patch.undo();
                    }
                },
            }),
            setBackOfficeSettingScope: builder.mutation<
                void,
                { key: string; scope: SettingScope; employeeIds: number[] }
            >({
                query: ({ key, scope, employeeIds }) => ({
                    url: `/v1/back-office-settings/${encodeURIComponent(key)}/scope`,
                    method: 'PUT',
                    body: { scope, employeeIds },
                }),
                invalidatesTags: ['BackOfficeSettings', 'RequiredFields'],
            }),
            setBackOfficeSettingOption: builder.mutation<void, { key: string; option: string; ticked: boolean }>({
                query: ({ key, option, ticked }) => ({
                    url: `/v1/back-office-settings/${encodeURIComponent(key)}/options/${encodeURIComponent(option)}`,
                    method: 'PUT',
                    body: { ticked },
                }),
                async onQueryStarted({ key, option, ticked }, { dispatch, queryFulfilled }) {
                    const patch = dispatch(
                        APIBackOfficeSettingsSlice.util.updateQueryData('getBackOfficeSettings', undefined, (draft) => {
                            const state = draft
                                .find((candidate) => candidate.key === key)
                                ?.options.find((candidate) => candidate.key === option);
                            if (state) state.ticked = ticked;
                        })
                    );
                    try {
                        await queryFulfilled;
                    } catch {
                        patch.undo();
                    }
                },
            }),
            getRequiredFields: builder.query<RequiredFieldElement[], void>({
                query: () => '/v1/back-office-settings/required-fields',
                providesTags: ['RequiredFields'],
            }),
            // Optimistic and invalidating nothing, as the setting ticks: a re-read between two quick ticks would undo one.
            setRequiredFieldMandatory: builder.mutation<
                void,
                { form: RequiredFieldForm; field: string; kind: string | null; mandatory: boolean }
            >({
                query: (body) => ({ url: '/v1/back-office-settings/required-fields/mandatory', method: 'PUT', body }),
                async onQueryStarted({ form, field, kind, mandatory }, { dispatch, queryFulfilled }) {
                    const patch = dispatch(
                        APIBackOfficeSettingsSlice.util.updateQueryData('getRequiredFields', undefined, (draft) => {
                            const entry = draft.find(
                                (candidate) =>
                                    candidate.form === form && candidate.field === field && candidate.kind === kind
                            );
                            if (entry) entry.mandatory = mandatory;
                        })
                    );
                    try {
                        await queryFulfilled;
                    } catch {
                        patch.undo();
                    }
                },
            }),
            setRequiredFieldAudience: builder.mutation<
                void,
                {
                    form: RequiredFieldForm;
                    field: string;
                    kind: string | null;
                    scope: RequiredFieldScope;
                    jobRoleIds: number[];
                    systemRoleIds: number[];
                    employeeIds: number[];
                }
            >({
                query: (body) => ({ url: '/v1/back-office-settings/required-fields/audience', method: 'PUT', body }),
                invalidatesTags: ['RequiredFields'],
            }),
            getEmployeePickers: builder.query<EmployeePickerElement[], void>({
                query: () => '/v1/back-office-settings/employee-pickers',
                providesTags: ['EmployeePickers'],
            }),
            // The pickers' option lists are cached under EmployeesMin: a stale one keeps offering who a row no longer lists.
            setEmployeePickerAudience: builder.mutation<
                void,
                {
                    form: EmployeePickerForm;
                    field: string;
                    scope: RequiredFieldScope;
                    jobRoleIds: number[];
                    employeeIds: number[];
                }
            >({
                query: (body) => ({ url: '/v1/back-office-settings/employee-pickers/audience', method: 'PUT', body }),
                invalidatesTags: ['EmployeePickers', 'EmployeesMin'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetBackOfficeSettingsQuery,
    useSetBackOfficeSettingEnabledMutation,
    useSetBackOfficeSettingScopeMutation,
    useSetBackOfficeSettingOptionMutation,
    useGetRequiredFieldsQuery,
    useSetRequiredFieldMandatoryMutation,
    useSetRequiredFieldAudienceMutation,
    useGetEmployeePickersQuery,
    useSetEmployeePickerAudienceMutation,
} = APIBackOfficeSettingsSlice;
